import { useEffect, useState } from "react";

import { Alert, AlertDescription, AlertTitle, Button, Dialog, Icon, InputField, Text, View } from "../base";
import { AgentEvaluatorAssignment, agentEvaluatorAssignmentActions } from "../../redux/reducers/agentEvaluatorAssignmentSlice";
import { Evaluator } from "../../redux/reducers/evaluatorSlice";
import { useAppDispatch } from "../../redux";
import { describeSamplingRate, parseSamplingRate, toPercentInputValue } from "../../utils/samplingRate";

export type AgentEvaluatorAssignmentDialogProps = {
  agentId: string;
  assignment: AgentEvaluatorAssignment | null;
  evaluator?: Evaluator | null;
  isOpen: boolean;
  onClose: () => void;
};

export const AgentEvaluatorAssignmentDialog = ({ agentId, assignment, evaluator, isOpen, onClose }: AgentEvaluatorAssignmentDialogProps) => {
  const dispatch = useAppDispatch();
  const [samplingInput, setSamplingInput] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (assignment) {
      setSamplingInput(toPercentInputValue(assignment.samplingRate));
      setError("");
    } else {
      setSamplingInput("");
      setError("");
    }
  }, [assignment]);

  const handleSave = async () => {
    if (!assignment) {
      return;
    }

    const { rate, error: validationError } = parseSamplingRate(samplingInput);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setIsSaving(true);

    try {
      await dispatch(
        agentEvaluatorAssignmentActions.updateAssignment({
          agentId,
          evaluatorId: assignment.evaluatorId,
          samplingRate: rate,
        }),
      ).unwrap();
      onClose();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to update assignment");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!assignment) {
      return;
    }

    setError("");
    setIsDeleting(true);

    try {
      await dispatch(
        agentEvaluatorAssignmentActions.deleteAssignment({
          agentId,
          evaluatorId: assignment.evaluatorId,
        }),
      ).unwrap();
      onClose();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "Unable to remove assignment");
    } finally {
      setIsDeleting(false);
    }
  };

  const samplingDescription = describeSamplingRate(assignment?.samplingRate ?? undefined);
  const updatedAt = assignment?.updatedAt ? new Date(assignment.updatedAt).toLocaleString() : null;
  const evaluatorUpdatedAt = evaluator?.updatedAt ? new Date(evaluator.updatedAt).toLocaleString() : null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => {
        if (!isSaving && !isDeleting) {
          onClose();
        }
      }}
      title="Assignment Details"
      actions={
        <View className="flex flex-row w-full justify-between">
          <Button
            variant="icon"
            className="text-error hover:text-error-dark"
            onClick={handleRemove}
            isDisabled={isSaving || isDeleting}
            isLoading={isDeleting}
          >
            Delete
          </Button>
          <Button
            variant="brand"
            onClick={handleSave}
            isDisabled={isSaving || isDeleting}
            isLoading={isSaving}
          >
            Save
          </Button>
        </View>
      }
    >
      <View className="flex flex-col gap-6 p-4">
        {evaluator ? (
          <View className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
            <View className="flex flex-col gap-1">
              <View className="flex flex-row w-full justify-between items-center">
                <Text className="text-lg font-semibold text-gray-900 dark:text-white">{evaluator.title}</Text>
                <Button
                  variant="icon"
                  className="text-brand hover:text-brand-dark"
                  aria-label="Open evaluator details"
                  onClick={(event) => {
                    event.stopPropagation();
                    window.open(`/evaluators/${evaluator.id}/details`, "_blank", "noopener,noreferrer");
                  }}
                >
                  <Icon
                    name="open"
                    size={16}
                  />
                </Button>
              </View>
              <View className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <View className="flex flex-col">
                  <Text className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Type</Text>
                  <Text className="text-gray-900 dark:text-white">{evaluator.evalType || "Unknown"}</Text>
                </View>
                <View className="flex flex-col">
                  <Text className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Target</Text>
                  <Text className="text-gray-900 dark:text-white">{evaluator.targetFormat || "Unknown"}</Text>
                </View>
              </View>
            </View>
            <View className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <View className="flex flex-col">
                <Text className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Last Updated</Text>
                <Text className="text-gray-900 dark:text-white">{evaluatorUpdatedAt || "Unknown"}</Text>
              </View>
            </View>
          </View>
        ) : (
          <Alert variant="warning">
            <AlertTitle>Evaluator unavailable</AlertTitle>
            <AlertDescription>This evaluator could not be found. You can still update or remove the assignment.</AlertDescription>
          </Alert>
        )}

        <View className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
          <View className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <View className="flex flex-col">
              <Text className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Sampling</Text>
              <Text className="text-gray-900 dark:text-white">{samplingDescription}</Text>
            </View>
            <View className="flex flex-col">
              <Text className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Last Updated</Text>
              <Text className="text-gray-900 dark:text-white">{updatedAt || "Unknown"}</Text>
            </View>
          </View>
        </View>

        <View className="flex flex-col bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-2">
          <InputField
            label="Sampling Rate (%)"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={samplingInput}
            onChange={(value) => {
              setSamplingInput(value);
              setError("");
            }}
            placeholder="100"
            error={error || undefined}
          />
          <Text className="text-xs text-gray-500 dark:text-gray-400">The percentage of agent sessions to evaluate</Text>
        </View>
      </View>
    </Dialog>
  );
};
